// Fine-Tune: pick one element of the resume and drag its margin while watching
// the lines rewrap. The server measures every margin once with the real fonts
// (stage 12, no Claude call), so the slider itself is instant and free.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { jobBullets } from '@/lib/resumeFit';
import { fitResumeLines } from '@/lib/measuredFit';
import { CheckCircle, Loader2, Sparkles, Target } from 'lucide-react';

const FIT_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd';
const FIT_APIKEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';

// Base right margins of the RDR template, in twips.
const BASE_RIGHT: Record<string, number> = { highlight: 144, pe_bullet: 0 };
const COLUMN_PT = 540;

type Kind = 'summary' | 'highlight' | 'pe_bullet';
type Line = { text: string; start: number; widthPt: number };
type Layout = { marginTw: number; widthPt: number; lines: Line[]; gapPt: number; lastFill: number; ok: boolean; bold: Array<[number, number]> };
type Element = { id: string; kind: Kind; group: string; label: string; text: string; chtitle?: string; match: string };

function elementsOf(json: any): Element[] {
  const out: Element[] = [];
  const s = String(json?.Summary ?? '').trim();
  if (s) out.push({ id: 's', kind: 'summary', group: 'Career Summary', label: 'Career Summary paragraph', text: s, match: '' });
  (json?.CareerHighlights ?? []).forEach((h: any, i: number) => {
    const text = String(h?.chdescription ?? '').trim();
    const chtitle = String(h?.chtitle ?? '').trim();
    if (text) out.push({ id: `h${i}`, kind: 'highlight', group: 'Career Highlights', label: chtitle || `Highlight ${i + 1}`, text, chtitle, match: `${chtitle}: ${text}`.slice(0, 48) });
  });
  (json?.ProfessionalExperience ?? []).forEach((j: any, ji: number) => {
    jobBullets(j).forEach((b: any, bi: number) => {
      const text = String(b?.Description ?? '').trim();
      if (text) out.push({ id: `e${ji}.${bi}`, kind: 'pe_bullet', group: j?.job_title || j?.company || `Job ${ji + 1}`, label: text.slice(0, 52), text, match: text.slice(0, 48) });
    });
  });
  return out;
}

function LineText({ line, bold }: { line: Line; bold: Array<[number, number]> }) {
  // Split the line into bold and regular runs by character position.
  const parts: Array<{ t: string; b: boolean }> = [];
  const inBold = (i: number) => bold.some(([a, z]) => i >= a && i < z);
  let cur = '', curB = inBold(line.start);
  for (let i = 0; i < line.text.length; i++) {
    const b = inBold(line.start + i);
    if (b !== curB) { parts.push({ t: cur, b: curB }); cur = ''; curB = b; }
    cur += line.text[i];
  }
  if (cur) parts.push({ t: cur, b: curB });
  return <>{parts.map((p, i) => (p.b ? <strong key={i}>{p.t}</strong> : <span key={i}>{p.t}</span>))}</>;
}

export function FineTunePanel({ resumeJson, templateId, onApply }: { resumeJson: any; templateId?: string; onApply: (next: any) => Promise<void> }) {
  const { toast } = useToast();
  const [json, setJson] = useState<any>(resumeJson);
  useEffect(() => { setJson(resumeJson); }, [resumeJson]);
  const elements = useMemo(() => elementsOf(json), [json]);
  const [selectedId, setSelectedId] = useState('s');
  const el = elements.find((e) => e.id === selectedId) ?? elements[0];

  const [layouts, setLayouts] = useState<Layout[] | null>(null);
  const [margin, setMargin] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewording, setRewording] = useState(false);
  const [error, setError] = useState('');
  const cache = useRef(new Map<string, Layout[]>());
  // The preview fills the panel, so the lines are easy to read.
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth));
    ro.observe(el);
    setBoxW(el.clientWidth);
    return () => ro.disconnect();
  });

  const savedMargin = (e: Element, list: Layout[]): number | null => {
    if (e.kind === 'summary') {
      if (typeof json?.SummaryIndentTwips === 'number') return json.SummaryIndentTwips;
      return list.find((l) => l.ok)?.marginTw ?? null; // the same search the Word file uses
    }
    const hit = (json?.LineMargins ?? []).find((m: any) => m.match === e.match);
    return hit ? hit.rightTwips - BASE_RIGHT[e.kind] : 0;
  };

  useEffect(() => {
    if (!el) return;
    let live = true;
    const key = `${el.kind}|${el.chtitle ?? ''}|${el.text}`;
    const show = (list: Layout[]) => {
      if (!live) return;
      setLayouts(list);
      setMargin(savedMargin(el, list) ?? 0);
    };
    const hit = cache.current.get(key);
    if (hit) { show(hit); return; }
    setLoading(true); setError(''); setLayouts(null);
    (async () => {
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        if (!token) throw new Error('not signed in');
        const res = await fetch(FIT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: FIT_APIKEY },
          body: JSON.stringify({ stage: 12, kind: el.kind, text: el.text, chtitle: el.chtitle }),
        });
        const j = await res.json().catch(() => null);
        if (!j?.success || !Array.isArray(j.layouts)) throw new Error(j?.error || 'no layouts');
        cache.current.set(key, j.layouts);
        show(j.layouts);
      } catch (e) {
        console.error('Fine-Tune measure failed:', e);
        if (live) setError("Sorry, we couldn't measure this right now. Please try again in a few minutes.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [el?.id, el?.text]); // eslint-disable-line react-hooks/exhaustive-deps

  if (templateId === 'original-format') {
    return <p className="text-sm text-muted-foreground">Fine-Tune measures the RDR templates. Switch to an RDR template to use it.</p>;
  }
  if (!elements.length) return <p className="text-sm text-muted-foreground">This document has no summary, highlights or bullets to fine-tune.</p>;

  const layout = layouts?.find((l) => l.marginTw === margin) ?? layouts?.[0] ?? null;
  const maxMargin = layouts?.[layouts.length - 1]?.marginTw ?? 0;
  const nearestOk = () => {
    if (!layouts) return null;
    const ok = layouts.filter((l) => l.ok);
    if (!ok.length) return null;
    return ok.reduce((a, b) => (Math.abs(b.marginTw - margin) < Math.abs(a.marginTw - margin) ? b : a));
  };
  const scale = boxW ? Math.min(1.6, (boxW - 16) / COLUMN_PT) : 0.72;
  const isSummary = el.kind === 'summary';
  const lineCount = layout?.lines.length ?? 0;
  const target = isSummary
    ? 'All lines the same length, with the last line within 5pt of the edge.'
    : el.kind === 'highlight' ? 'Exactly 2 lines, with the second line at least 82% full.' : 'Exactly 1 line, at least 88% full.';

  const apply = async () => {
    if (!layout) return;
    setSaving(true);
    try {
      const next = structuredClone(json);
      if (isSummary) next.SummaryIndentTwips = margin;
      else {
        const rest = (next.LineMargins ?? []).filter((m: any) => m.match !== el.match);
        next.LineMargins = margin === 0 ? rest : [...rest, { match: el.match, rightTwips: BASE_RIGHT[el.kind] + margin }];
      }
      await onApply(next);
      setJson(next);
      toast({ title: 'Saved to your Word document', description: `${el.label}: margin updated.` });
    } catch (e) {
      console.error('Fine-Tune save failed:', e);
      toast({ title: "Sorry, we couldn't save that right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Margins alone cannot fix everything: reword this one element, keeping every
  // fact, until it fits its rule. Small paid call.
  const reword = async () => {
    setRewording(true);
    try {
      // Blank the element in the "before" copy so only it is sent to be fitted.
      const before = structuredClone(json);
      if (el.kind === 'summary') before.Summary = '';
      else if (el.kind === 'highlight') before.CareerHighlights[Number(el.id.slice(1))].chdescription = '';
      else { const [ji, bi] = el.id.slice(1).split('.').map(Number); jobBullets(before.ProfessionalExperience[ji])[bi].Description = ''; }
      const fit = await fitResumeLines(json, before, { passedOnly: true });
      const textOf = (j: any) => elementsOf(j).find((e) => e.id === el.id)?.text;
      if (textOf(fit.json) === el.text) {
        toast({ title: 'No better wording found', description: 'The current wording is the closest. Try the slider.' });
      } else {
        const next = { ...fit.json };
        if (isSummary) delete next.SummaryIndentTwips;
        setJson(next);
        toast({ title: 'New wording ready', description: 'Check the preview, then press Save to Word doc.' });
      }
    } catch (e) {
      console.error('Summary reword failed:', e);
      toast({ title: "Sorry, we couldn't reword this right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
    } finally {
      setRewording(false);
    }
  };

  const groups = [...new Set(elements.map((e) => e.group))];
  const best = nearestOk();
  const wordingChanged = JSON.stringify(elementsOf(json).map((e) => e.text)) !== JSON.stringify(elementsOf(resumeJson).map((e) => e.text));

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-semibold text-foreground" htmlFor="ft-element">Choose what to adjust</label>
        <select id="ft-element" value={el.id} onChange={(e) => setSelectedId(e.target.value)} className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm">
          {groups.map((g) => (
            <optgroup key={g} label={g}>
              {elements.filter((e) => e.group === g).map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Measuring...</div>}
      {error && <p className="text-sm text-red-700">{error}</p>}

      {layout && (
        <>
          <div ref={boxRef} className="overflow-x-auto rounded-md border bg-white py-3">
            <div className="relative mx-auto" style={{ width: COLUMN_PT * scale }}>
              {layout.lines.map((l, i) => (
                <div
                  key={i}
                  className="overflow-hidden whitespace-nowrap text-black"
                  style={{
                    width: l.widthPt * scale,
                    marginLeft: isSummary ? (margin / 20) * scale : el.kind === 'highlight' ? (648 / 20) * scale : 21.6 * scale,
                    fontFamily: 'Aptos, Calibri, "Segoe UI", sans-serif',
                    fontSize: 10.2 * scale,
                    lineHeight: 1.35,
                    textAlign: 'justify',
                    textAlignLast: 'justify',
                  }}
                >
                  <LineText line={l} bold={layout.bold} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{isSummary ? 'Margin, each side' : 'Right margin'}</span>
              <span className="text-muted-foreground">{(margin / 1440).toFixed(2)} in</span>
            </div>
            <Slider className="mt-2" min={0} max={maxMargin} step={10} value={[margin]} onValueChange={(v) => setMargin(v[0])} aria-label="Margin" />
          </div>

          <div className={`rounded-md border px-3 py-2 text-sm ${layout.ok ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
            {layout.ok ? (
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle className="h-4 w-4" /> {isSummary ? 'Perfect rectangle' : 'Fits the rule'}</span>
            ) : <span className="font-medium">Not there yet</span>}
            <div className="text-xs mt-0.5">
              {lineCount} line{lineCount === 1 ? '' : 's'}, last line ends {layout.gapPt}pt from the edge. {target}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!best || best.marginTw === margin} onClick={() => best && setMargin(best.marginTw)}>
              <Target className="h-4 w-4" /> {best ? 'Jump to best fit' : 'No margin fits'}
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={rewording} onClick={reword} title="Uses a small paid Claude call.">
              {rewording ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Suggest a rewording
            </Button>
            <Button size="sm" disabled={saving} onClick={apply} className="gap-1.5">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save to Word doc
            </Button>
          </div>
          {wordingChanged && <p className="text-xs text-muted-foreground">There is new wording that is not saved yet.</p>}
        </>
      )}
    </div>
  );
}
